// Escaping / sanitising helpers for the block compiler. Everything a user can
// type reaches the email through one of these.

const HTML_ESC: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

export function escapeHtml(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, (c) => HTML_ESC[c])
}

export function escapeAttr(v: unknown): string {
  return escapeHtml(v).replace(/`/g, '&#96;')
}

// `{{ cart_url }}` (inner whitespace) is accepted and normalised to
// `{{cart_url}}`, matching the backend's _SAFE_URL.
const TOKEN_ONLY = /^\{\{\s*([a-z_]+)\s*\}\}$/
const normToken = (s: string) => s.replace(TOKEN_ONLY, '{{$1}}')

// Allowed: http(s), mailto, or exactly one {{token}}. Anything else → '#'.
// The result is attribute-escaped.
export function safeUrl(v: unknown): string {
  const s = String(v ?? '').trim()
  if (!s) return '#'
  if (TOKEN_ONLY.test(s)) return normToken(s)
  if (/^(https?:\/\/|mailto:)/i.test(s)) {
    // Reject control characters / whitespace tricks inside the url.
    // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional
    if (/[\u0000-\u001F\u007F\s]/.test(s)) return '#'
    return escapeAttr(s)
  }
  return '#'
}

// Image sources: http(s) or exactly one {{token}}; otherwise ''.
export function safeImgUrl(v: unknown): string {
  const s = String(v ?? '').trim()
  if (!s) return ''
  if (TOKEN_ONLY.test(s)) return normToken(s)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional
  if (/^https?:\/\//i.test(s) && !/[\u0000-\u001F\u007F\s]/.test(s)) {
    return escapeAttr(s)
  }
  return ''
}

// Safe CSS colour (hex, rgb/rgba/hsl/hsla, named, transparent); else fallback.
export function safeColor(v: unknown, fallback: string): string {
  const s = String(v ?? '').trim()
  if (!s) return fallback
  if (/^#[0-9a-f]{3,8}$/i.test(s)) return s
  if (/^(rgb|rgba|hsl|hsla)\([0-9.,%\s]+\)$/i.test(s)) return s
  if (/^[a-z]{3,20}$/i.test(s)) return s
  return fallback
}

// Font-family stack: letters, digits, spaces, commas, quotes, hyphens.
export function safeFont(v: unknown, fallback: string): string {
  const s = String(v ?? '').trim()
  if (!s || !/^[\w\s,'-]+$/.test(s)) return fallback
  return s
}

export function safeNum(
  v: unknown,
  fallback: number,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
): number {
  // A cleared input ('' / null / undefined) means "use the default".
  if (v == null || (typeof v === 'string' && !v.trim())) return fallback
  const n = Number(v)
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, n))
}

export function pick<T extends string>(
  v: unknown,
  allowed: readonly T[],
  fallback: T,
): T {
  return (allowed as readonly string[]).includes(String(v))
    ? (v as T)
    : fallback
}

// ── Rich text ────────────────────────────────────────────────────────────────

const ALLOWED_TAGS = new Set([
  'b',
  'strong',
  'i',
  'em',
  'u',
  'a',
  'br',
  'ul',
  'ol',
  'li',
  'span',
])
// Dropped together with their content.
const DROP_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
  'svg',
  'math',
  'head',
  'title',
  'textarea',
  'select',
  'button',
  'form',
  'input',
  'link',
  'meta',
])
// Unwrapped, but they end a line (contenteditable produces div/p).
const BLOCK_TAGS = new Set([
  'p',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'section',
  'article',
  'header',
  'footer',
  'tr',
])

const SPAN_STYLE_PROP = /^(color|font-[a-z-]+|letter-spacing)$/

function cleanSpanStyle(style: string): string {
  const out: string[] = []
  for (const decl of style.split(';')) {
    const i = decl.indexOf(':')
    if (i < 0) continue
    const prop = decl.slice(0, i).trim().toLowerCase()
    const val = decl.slice(i + 1).trim()
    if (!SPAN_STYLE_PROP.test(prop) || !val) continue
    if (/expression\s*\(|javascript:|url\s*\(|[<>"\\]/i.test(val)) continue
    out.push(`${prop}:${val}`)
  }
  return out.join(';')
}

function walk(node: Node, out: string[]): void {
  node.childNodes.forEach((child, idx) => {
    if (child.nodeType === 3) {
      out.push(escapeHtml(child.textContent ?? ''))
      return
    }
    if (child.nodeType !== 1) return
    const el = child as Element
    const tag = el.tagName.toLowerCase()
    if (DROP_TAGS.has(tag)) return
    if (ALLOWED_TAGS.has(tag)) {
      if (tag === 'br') {
        out.push('<br>')
        return
      }
      let attrs = ''
      if (tag === 'a') {
        attrs = ` href="${safeUrl(el.getAttribute('href'))}"`
      } else if (tag === 'span') {
        const st = cleanSpanStyle(el.getAttribute('style') ?? '')
        if (st) attrs = ` style="${escapeAttr(st)}"`
      }
      out.push(`<${tag}${attrs}>`)
      walk(el, out)
      out.push(`</${tag}>`)
      return
    }
    // Disallowed element: keep its text content.
    walk(el, out)
    const isLast = idx === node.childNodes.length - 1
    if (BLOCK_TAGS.has(tag) && !isLast) out.push('<br>')
  })
}

// Allows only b strong i em u a[href] br ul ol li span[style colour/font/letter-spacing].
export function sanitizeInlineHtml(html: unknown): string {
  const src = String(html ?? '')
  if (!src) return ''
  if (typeof DOMParser === 'undefined') {
    // Non-DOM environment: fall back to plain text.
    return escapeHtml(src.replace(/<[^>]*>/g, ''))
  }
  const doc = new DOMParser().parseFromString(
    `<!doctype html><body>${src}</body>`,
    'text/html',
  )
  const out: string[] = []
  walk(doc.body, out)
  return out.join('')
}

// Hex-only colour, normalised to #rrggbb (#rgb expanded); else fallback.
export function safeHex(v: unknown, fallback: string): string {
  const s = String(v ?? '')
    .trim()
    .replace(/^#/, '')
  if (/^[0-9a-f]{3}$/i.test(s))
    return `#${s
      .split('')
      .map((c) => c + c)
      .join('')}`
  if (/^[0-9a-f]{6}$/i.test(s)) return `#${s}`
  return fallback
}
